import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Cpu, ShieldCheck, GraduationCap, Layers } from 'lucide-react';

const services = [
  {
    id: 'cross-platform',
    title: 'Cross-Platform Development',
    blurb:
      'One codebase, every surface. Native-feeling apps for iOS, Android, and the web with a shared design system and release pipeline.',
    icon: Layers,
    span: 'md:col-span-7 md:row-span-2',
    image:
      'https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?w=1200&q=80',
  },
  {
    id: 'ai-integration',
    title: 'AI Integration',
    blurb:
      'Ship LLM, RAG and agent features inside real products — with evaluation, observability and cost controls baked in.',
    icon: Cpu,
    span: 'md:col-span-5',
  },
  {
    id: 'ai-security',
    title: 'AI Security',
    blurb:
      'Red-team your model surface. Prompt injection, data exfiltration and tool-use risks — assessed, mitigated, monitored.',
    icon: ShieldCheck,
    span: 'md:col-span-5',
  },
  {
    id: 'ai-training',
    title: 'AI Training',
    blurb:
      'Bring your engineers and leaders up the curve. Hands-on programs on building, evaluating and securing AI systems.',
    icon: GraduationCap,
    span: 'md:col-span-7',
  },
];

const stats = [
  { value: '11+', label: 'Years shipping software' },
  { value: '42', label: 'Products in production' },
  { value: '4', label: 'Continents served' },
  { value: '0', label: 'Security incidents' },
];

const stack = [
  'React', 'React Native', 'Next.js', 'TypeScript', 'Python', 'FastAPI',
  'PostgreSQL', 'OpenAI', 'Anthropic', 'LangGraph', 'AWS', 'Cloudflare',
  'Kubernetes', 'Terraform', 'Sanity', 'Stripe',
];

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 ink-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-32 lg:pb-40 relative">
          <div className="flex items-center gap-3 mb-8 animate-fade-up">
            <span className="h-1.5 w-1.5 bg-ember rounded-full" />
            <span className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
              Software Engineering Studio · Sydney
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-bold tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-ink max-w-5xl animate-fade-up"
            style={{ animationDelay: '80ms' }}
          >
            We build software that
            <br />
            <span className="italic font-light">behaves</span> in production.
          </h1>

          <p
            className="mt-8 text-lg md:text-xl text-ink-muted max-w-2xl leading-relaxed animate-fade-up"
            style={{ animationDelay: '160ms' }}
          >
            Veventex is a small senior team designing, shipping and securing
            cross-platform products and AI systems for teams that care about
            doing it properly the first time.
          </p>

          <div
            className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up"
            style={{ animationDelay: '240ms' }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-ink text-cream px-6 py-3.5 text-sm font-medium hover:bg-ember transition-colors"
            >
              Start a project
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-ink/20 px-6 py-3.5 text-sm font-medium hover:border-ink transition-colors"
            >
              See our services
            </Link>
          </div>

          {/* stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {stats.map((s) => (
              <div key={s.label} className="bg-cream p-6 md:p-8" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-ink">
                  {s.value}
                </div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-black/10 bg-cream-alt overflow-hidden py-6">
        <div className="flex gap-12 whitespace-nowrap animate-marquee">
          {[...stack, ...stack].map((s, i) => (
            <span
              key={i}
              className="font-mono text-xs tracking-widest uppercase text-ink-muted"
            >
              {s} <span className="text-ember mx-3">/</span>
            </span>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
              01 · What we do
            </div>
            <h2 className="font-display font-bold tracking-tighter text-4xl md:text-6xl text-ink max-w-2xl">
              Four practices.<br />One studio.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-section-cta"
            className="font-mono text-xs tracking-widest uppercase border-b border-ink pb-1 hover:text-ember hover:border-ember transition-colors"
          >
            Explore all services →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[minmax(220px,auto)]">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.id}
                to={`/services#${s.id}`}
                data-testid={`service-card-${s.id}`}
                className={`group relative bg-cream border border-black/10 p-8 md:p-10 flex flex-col justify-between overflow-hidden hover:border-ink transition-all hover:-translate-y-0.5 ${s.span}`}
              >
                {s.image && (
                  <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                    <img src={s.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="relative">
                  <Icon size={28} strokeWidth={1.25} className="text-ink mb-6" />
                  <h3 className="font-display font-semibold tracking-tight text-2xl md:text-3xl text-ink mb-3">
                    {s.title}
                  </h3>
                  <p className="text-ink-muted max-w-md leading-relaxed">{s.blurb}</p>
                </div>
                <div className="relative mt-6 flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-ink group-hover:text-ember transition-colors">
                  Learn more
                  <ArrowUpRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="border-t border-black/10 bg-cream-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
              02 · How we work
            </div>
            <h2 className="font-display font-bold tracking-tighter text-4xl md:text-5xl text-ink">
              Small senior team.<br />
              <span className="italic font-light">Boring, reliable delivery.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {[
              { t: 'Senior-only engineers', d: 'Every line of code is written or reviewed by someone who has shipped at scale.' },
              { t: 'Evaluation before launch', d: 'No AI feature ships without a measurable eval suite and a rollback plan.' },
              { t: 'Security by default', d: 'Threat modelling, secrets hygiene and least privilege are part of week one.' },
              { t: 'Hand-over you can read', d: 'Documentation your future team can follow, not just the one we built it with.' },
            ].map((p) => (
              <div key={p.t} className="bg-cream p-8">
                <div className="font-display font-semibold text-xl text-ink mb-2">{p.t}</div>
                <div className="text-ink-muted leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="border border-black/10 p-10 md:p-16 bg-cream flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
              03 · Next step
            </div>
            <h2 className="font-display font-bold tracking-tighter text-4xl md:text-6xl text-ink max-w-2xl">
              Have a hard problem?<br />Let&apos;s scope it together.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="cta-section-button"
            className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm font-medium hover:bg-ember transition-colors whitespace-nowrap"
          >
            Book an intro call
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
